// =====================================================
// SUBMIT DEAL FORM TRANSLATIONS (11-Step Master Form)
// =====================================================

export const SUBMIT_DEAL_TRANSLATIONS = {
  en: {
    // Page Header
    pageTitle: "Deal Submission Form",
    pageSubtitle: "Submit your business for acquisition or financing evaluation",
    stepOf: "Step {current} of {total}",
    
    // Navigation
    previous: "Previous",
    next: "Next",
    saveDraft: "Save Draft",
    submit: "Submit Deal",
    
    // Step Titles
    steps: {
      step1: "General Information",
      step2: "Operations & Owner Dependency",
      step3: "Licenses & Compliance",
      step4: "Assets & Balance Sheet",
      step5: "Real Estate",
      step6: "Financial Statements",
      step7: "Add-Backs (EBITDA Adjustments)",
      step8: "Projections & Growth",
      step9: "Culture & Transition",
      step10: "SWOT Analysis",
      step11: "Business Attractiveness",
      step12: "Documents & Confidentiality",
    },

    // Step 1: General Information
    step1: {
      legalName: "Legal Business Name",
      legalNamePlaceholder: "Enter legal business name",
      tradeName: "Trade Name (DBA)",
      tradeNamePlaceholder: "Enter trade name if different",
      entityType: "Entity Type",
      entityTypePlaceholder: "Select entity type...",
      entityTypes: {
        llc: "LLC",
        cCorp: "C Corporation",
        sCorp: "S Corporation",
        partnership: "Partnership",
        soleProprietorship: "Sole Proprietorship",
        other: "Other",
      },
      ownershipCount: "Number of Owners",
      primaryOwnerPercent: "Primary Owner's Ownership Percentage (%)",
      primaryOwnerPercentHint: "If applicable, percentage held by the primary owner",
      dealTitle: "Deal Title",
      dealTitlePlaceholder: "Enter a title for this deal",
      description: "Detailed Business Description",
      descriptionPlaceholder: "Describe your business in detail (minimum 50 characters)",
      descriptionHint: "Minimum 50 characters required",
      reasonForSale: "Reason for Sale",
      reasonForSalePlaceholder: "Explain why you are selling the business",
      industry: "Industry / Sector",
      industryPlaceholder: "Select industry...",
      naicsCode: "NAICS Code",
      naicsCodePlaceholder: "Industry classification code",
      city: "City",
      cityPlaceholder: "Enter city",
      state: "State / Province",
      statePlaceholder: "Enter state or province",
      country: "Country",
      countryPlaceholder: "Select country...",
      yearEstablished: "Year Established",
      yearEstablishedHint: "The business must be at least 3 years old to be eligible.",
      previouslyListed: "Has this business been listed for sale in the last 3 years?",
      previouslyListedYes: "Yes",
      previouslyListedNo: "No",
      previouslyListedNote: "This information is used exclusively by AcquizGroup. It will not be published publicly, but helps us develop services better tailored to sellers' needs.",
      representedSeller: "Represented Seller",
      representedSellerHint: "Select which seller you are submitting this deal on behalf of",
      representedSellerPlaceholder: "Select a represented seller...",
      noRepresentedSellers: "You have no represented sellers yet. Add one from your dashboard first.",
    },

    // Step 2: Operations & Owner Dependency
    step2: {
      sectionTitle: "Operations & Owner Dependency",
      fullTimeEmployees: "Full-Time Employees",
      partTimeEmployees: "Part-Time Employees",
      dailyOperationsDescription: "Daily Operations Description",
      dailyOperationsPlaceholder: "Describe how the business operates on a daily basis",
      processDocumentationLevel: "Process Documentation Level",
      documentationLevels: {
        1: "1 - No documentation",
        2: "2 - Minimal documentation",
        3: "3 - Partial documentation",
        4: "4 - Well documented",
        5: "5 - Fully documented (Complete SOPs)",
      },
      customerConcentration: "Customer Concentration (%)",
      customerConcentrationHint: "Percentage of revenue from largest customer",
      ownerInvolvement: "Owner Involvement",
      ownerHoursPerWeek: "Owner Hours Per Week",
      ownerType: "Owner Type",
      ownerTypes: {
        active: "Active (daily presence)",
        semiActive: "Semi-active (partial presence)",
        absentee: "Absentee (full delegation)",
      },
      ownerCriticalTasks: "Owner's Critical Tasks",
      ownerCriticalTasksPlaceholder: "Describe the tasks that only the owner can perform",
      ownerAbsenceImpact: "Impact of Owner's Absence",
      ownerAbsenceImpactPlaceholder: "What happens if the owner is absent for an extended period?",
    },

    // Step 3: Licenses & Compliance
    step3: {
      sectionTitle: "Licenses, Compliance & Technology",
      specialLicensesRequired: "Are special licenses required to operate this business?",
      yes: "Yes",
      no: "No",
      licenseDetails: "License and Permit Details",
      licenseDetailsPlaceholder: "Describe required licenses and permits",
      regulatoryRequirements: "Regulatory Requirements",
      regulatoryRequirementsPlaceholder: "Describe any regulatory requirements",
      legalDisputes: "Ongoing or Past Legal Disputes",
      legalDisputesPlaceholder: "Describe any legal disputes (past or ongoing)",
      technologySectionTitle: "Technology & Tools",
      hasProprietaryTech: "Does the business own proprietary technology (platform, software, app)?",
      proprietaryTechDescription: "Proprietary Technology Description",
      proprietaryTechDescriptionPlaceholder: "Describe the platform, software, or tools you have built",
      techStackDescription: "Technology Stack",
      techStackDescriptionPlaceholder: "e.g., languages, frameworks, cloud provider, key integrations",
      thirdPartyTools: "Third-Party Software & Tools Used",
      thirdPartyToolsPlaceholder: "List the software/tools used to run the business (CRM, accounting, payments, etc.)",
      toolsTransferable: "Are these subscriptions/licenses transferable to a new owner?",
      requiredBuyerTechSkills: "Technical Skills Required From Buyer",
      requiredBuyerTechSkillsPlaceholder: "e.g., \"No coding required\" or \"Basic familiarity with X needed\"",
      hostingProvider: "Hosting Provider (if applicable)",
      hostingProviderPlaceholder: "e.g., AWS, Firebase, Shopify",
    },

    // Step 4: Assets & Balance Sheet
    step4: {
      sectionTitle: "Assets & Balance Sheet",
      inventoryValue: "Inventory Value ($)",
      ffeValue: "FF&E Value ($)",
      ffeValueHint: "Furniture, Fixtures & Equipment",
      accountsReceivable: "Accounts Receivable ($)",
      otherCurrentAssets: "Other Current Assets ($)",
      patentsOrTrademarks: "Does the business own patents or trademarks?",
      intellectualPropertyDetails: "Intellectual Property Details",
      intellectualPropertyPlaceholder: "Describe any patents, trademarks, or proprietary technology",
    },

    // Step 5: Real Estate
    step5: {
      sectionTitle: "Real Estate",
      isIncluded: "Is real estate included in the transaction?",
      includedOptions: {
        yes: "Yes, real estate is included",
        no: "No, the business leases its premises",
      },
      realEstateAskPrice: "Real Estate Asking Price ($)",
      monthlyRent: "Monthly Rent ($)",
      leaseExpirationDate: "Lease Expiration Date",
    },

    // Step 6: Financial Statements
    step6: {
      sectionTitle: "Financial Statements",
      askingPrice: "Business Asking Price ($)",
      askingPriceHint: "Asking price for the business (excluding real estate if separate)",
      revenueSection: "Revenue (Last 3 Years)",
      revenueYear1: "Year 1 (most recent)",
      revenueYear2: "Year 2",
      revenueYear3: "Year 3",
      ebitdaSection: "EBITDA (Last 3 Years)",
      ebitdaYear1: "EBITDA Year 1",
      ebitdaYear2: "EBITDA Year 2",
      ebitdaYear3: "EBITDA Year 3",
      debtEquitySection: "Debt & Equity",
      cashFlowEquity: "Cash Flow / Owner's Equity ($)",
      cashFlowEquityHint: "This amount MUST be positive. Businesses with negative cash flow will be AUTOMATICALLY REJECTED.",
      totalLiabilities: "Total Liabilities ($)",
      inheritedDebt: "Debt Assumed by Buyer ($)",
      inheritedDebtHint: "Debt that the new buyer will need to assume",
    },

    // Step 7: Add-Backs
    step7: {
      sectionTitle: "Add-Backs (EBITDA Adjustments)",
      sectionHint: "Add-backs represent discretionary or personal expenses of the current owner that the new owner will likely not incur.",
      ownerSalary: "Owner's Salary ($)",
      autoExpense: "Personal Auto Expenses ($)",
      travelEntertainment: "Travel & Entertainment ($)",
      nonRecurringLegal: "Non-Recurring Legal Fees ($)",
      otherDiscretionary: "Other Discretionary Expenses ($)",
      addbackRationale: "Detailed Add-Back Justification",
      addbackRationalePlaceholder: "Provide detailed justification for each add-back claimed",
    },

    // Step 8: Projections & Growth
    step8: {
      sectionTitle: "Projections & Growth",
      revenueGrowthForecast: "Revenue Growth Forecast (%)",
      revenueGrowthHint: "Expected annual revenue growth percentage",
      ebitdaGrowthForecast: "EBITDA Growth Forecast (%)",
      ebitdaGrowthHint: "Expected annual EBITDA growth percentage",
      growthOpportunities: "Identified Growth Opportunities",
      growthOpportunitiesPlaceholder: "Describe specific opportunities for growth",
      sellerFinancing: "Seller Financing",
      sellerFinancingOffered: "Are you open to seller financing?",
      sellerFinancingDetails: "Proposed Seller Financing Details",
      sellerFinancingPlaceholder: "Describe terms you would consider (down payment %, interest rate, term length)",
      sfAmount: "Seller Financing Amount ($)",
      sfAmountHint: "Total amount you are willing to finance",
      sfPercentage: "Percentage of Sale Price (%)",
      sfPercentageHint: "Portion of the asking price covered by seller financing",
      sfInterestRate: "Interest Rate (%)",
      sfInterestRateHint: "Annual interest rate on seller note",
      sfTermMonths: "Loan Term (months)",
      sfTermHint: "Duration of the seller financing note",
      sfDownPayment: "Required Down Payment (%)",
      sfDownPaymentHint: "Minimum down payment required from buyer",
      sfStandby: "Standby Period (months)",
      sfStandbyHint: "Period where no payments are due (0 if none)",
      sfCollateral: "Collateral / Security",
      sfCollateralHint: "What secures the seller note?",
      sfCollateralOptions: {
        businessAssets: "Business Assets",
        personalGuarantee: "Personal Guarantee",
        realEstate: "Real Estate",
        stockPledge: "Stock / Equity Pledge",
        none: "None / Unsecured",
      },
      sfNotes: "Additional Terms & Conditions",
      sfNotesPlaceholder: "Any special conditions, earn-outs, performance triggers, etc.",
    },

    // Step 9: Culture & Transition
    step9: {
      sectionTitle: "Culture & Transition",
      coreValues: "Core Business Values (up to 5)",
      coreValuesHint: "Core business values are the fundamental, non-negotiable guiding principles that define your organization's identity, culture, and decision-making.",
      coreValueLabels: ["Integrity", "Customer-Centricity", "Innovation", "Accountability", "Collaboration / Teamwork"],
      coreValuePlaceholder: "Describe this value...",
      coreValueDescriptionPlaceholder: "Describe how this value is applied in your business...",
      managementTeam: "Management Team",
      managementTeamExplanation: "Please explain your management team structure, key roles, and strengths",
      idealSuccessor: "Ideal Successor Description",
      idealSuccessorPlaceholder: "Describe the ideal buyer profile for your business",
      biggestFear: "Your Biggest Concern About the Sale",
      biggestFearPlaceholder: "What concerns you most about selling?",
      transitionPeriod: "Transition Period",
      postSaleConsulting: "Post-Sale Consulting Availability",
      consultingOptions: {
        yes: "Yes",
        no: "No",
        negotiable: "Negotiable",
      },
      transitionDuration: "Preferred Transition Duration",
      transitionOptions: {
        30: "30 days",
        60: "60 days",
        90: "90 days",
        more: "More than 90 days",
      },
      nonCompetePeriod: "Non-Compete Period",
      nonCompetePeriodOptions: {
        none: "None",
        "1_year": "1 year",
        "2_years": "2 years",
        "3_years": "3 years",
        "5_years": "5 years",
        negotiable: "Negotiable",
      },
      dealBreakers: "Non-Negotiable Terms (Deal Breakers)",
      dealBreakersPlaceholder: "List any terms that are non-negotiable for you",
    },

    // Step 10: SWOT Analysis
    step10: {
      sectionTitle: "SWOT Analysis",
      sectionHint: "Please provide a detailed analysis of the business's strengths, weaknesses, opportunities and threats. Minimum 10 characters per section.",
      strengths: "Strengths",
      strengthsPlaceholder: "Describe the key strengths of your business",
      weaknesses: "Weaknesses",
      weaknessesPlaceholder: "Describe the main weaknesses or challenges",
      opportunities: "Opportunities",
      opportunitiesPlaceholder: "Describe growth opportunities and market potential",
      threats: "Threats",
      threatsPlaceholder: "Describe external threats and competitive challenges",
    },

    // Step 11: Documents & Confidentiality
    step11: {
      sectionTitle: "Documents & Confidentiality",
      sectionHint: "These settings control the visibility and access to your deal information in the Deal Monitor.",
      confidentialityLevel: "Confidentiality Level",
      confidentialityOptions: {
        confidential: "Confidential - Access by request only",
        public: "Public - Visible to all qualified buyers",
      },
      confidentialityNote: "All deals are treated confidentially to protect your sensitive information.",
      lendersShareConsent: "Your deal information will be shared with qualified lenders in our network",
      lendersShareConsentHint: "This enables automatic matching with appropriate lenders for your deal — a standard part of every listing on AcquizGroup™.",
      documentUpload: "Document Upload",
      financialDocuments: "Financial and Legal Documents",
      financialDocumentsHint: "Accepted formats: PDF, Word, Excel",
      businessPhotos: "Business Photos",
      businessLogo: "Business Logo",
      businessLogoHint: "Square format recommended (300x300px minimum)",
      remove: "Remove",
      anonymization: "Public Listing Anonymization",
      hideLegalName: "Hide legal business name",
      hideExactLocation: "Hide exact location",
      hideOwnerName: "Hide owner name",
    },

    // Validation Messages
    validation: {
      required: "This field is required",
      legalNameRequired: "Legal business name is required",
      legalNameMinLength: "Legal name must be at least 2 characters",
      entityTypeRequired: "Entity type is required",
      titleRequired: "Deal title is required",
      titleMinLength: "Title must be at least 3 characters",
      descriptionRequired: "Description is required",
      descriptionMinLength: "Description must be at least 50 characters",
      industryRequired: "Industry is required",
      cityRequired: "City is required",
      countryRequired: "Country is required",
      yearEstablishedRequired: "Year established is required",
      businessTooYoung: "Business must be at least 3 years old. Submission will be AUTOMATICALLY REJECTED.",
      ownerHoursInvalid: "Invalid number of hours (0-168)",
      askingPriceMinimum: "Minimum asking price of $1,000",
      cashFlowPositive: "Cash flow / Owner's equity MUST be positive. Negative value = AUTOMATIC REJECTION.",
      swotMinLength: "{field} required (minimum 10 characters)",
    },

    // Industries (matching seller registration)
    industries: [
      "Agriculture, Agri-Food & Food Industry",
      "Automotive & Boat / Marine",
      "Beauty & Personal Care",
      "Retail (General, Sports, Specialized)",
      "Communication, Media & Public Relations",
      "Engineering & Technical Consulting",
      "Construction & Building (incl. Trades & HVAC)",
      "Entertainment, Leisure & Sports",
      "Education & Children Services",
      "Online & Technology (SaaS, Web Dev, IT Services)",
      "Service Businesses (General)",
      "Manufacturing & Industrial Production",
      "Accounting & Tax Firm (CPA, CA)",
      "Legal Firm (Corporate, IP, Real Estate)",
      "Waste Management, Water Treatment & Environment",
      "Wholesalers & Distributors",
      "Business Intelligence & Data Consulting",
      "Unclassifiable / Other",
      "Recruitment & Human Resources",
      "Restaurants & Food Services",
      "Home Services (Residential & Commercial Contractors)",
      "Financial Services & Insurance",
      "Real Estate Services & Property Management",
      "Pet Services",
      "Healthcare, Medical & Life Sciences",
      "Transportation, Trucking & Logistics",
      "Travel, Tourism & Hospitality",
    ],

    // Countries
    countries: [
      "Canada",
      "United States",
      "France",
      "United Kingdom",
      "Germany",
      "Australia",
      "Other",
    ],

    // Success/Error Messages
    messages: {
      draftSaved: "Draft saved successfully!",
      draftSavedDescription: "You can resume your submission later.",
      submitSuccess: "Deal submitted successfully!",
      submitSuccessDescription: "Your deal has been submitted for review.",
      submitError: "Failed to submit deal. Please try again.",
      authRequired: "Please sign in to submit a deal",
    },
  },

  fr: {
    // Page Header
    pageTitle: "Formulaire de Soumission",
    pageSubtitle: "Soumettez votre entreprise pour évaluation d'acquisition ou de financement",
    stepOf: "Étape {current} sur {total}",
    
    // Navigation
    previous: "Précédent",
    next: "Suivant",
    saveDraft: "Sauvegarder le Brouillon",
    submit: "Soumettre",
    
    // Step Titles
    steps: {
      step1: "Informations Générales",
      step2: "Opérations et Dépendance au Propriétaire",
      step3: "Licences et Conformité",
      step4: "Actifs et Bilan",
      step5: "Immobilier",
      step6: "États Financiers",
      step7: "Add-Backs (Ajustements EBITDA)",
      step8: "Projections et Croissance",
      step9: "Culture et Transition",
      step10: "Analyse SWOT",
      step11: "Attractivité de l'Entreprise",
      step12: "Documents et Confidentialité",
    },

    // Step 1: General Information
    step1: {
      legalName: "Raison Sociale",
      legalNamePlaceholder: "Entrez la raison sociale",
      tradeName: "Nom Commercial (DBA)",
      tradeNamePlaceholder: "Entrez le nom commercial si différent",
      entityType: "Type d'Entité",
      entityTypePlaceholder: "Sélectionnez le type d'entité...",
      entityTypes: {
        llc: "SARL",
        cCorp: "Société Anonyme (SA)",
        sCorp: "SAS",
        partnership: "Société en Nom Collectif",
        soleProprietorship: "Entreprise Individuelle",
        other: "Autre",
      },
      ownershipCount: "Nombre de Propriétaires",
      primaryOwnerPercent: "Pourcentage de Propriété du Propriétaire Principal (%)",
      primaryOwnerPercentHint: "Si applicable, pourcentage détenu par le propriétaire principal",
      dealTitle: "Titre du Deal",
      dealTitlePlaceholder: "Entrez un titre pour ce deal",
      description: "Description Détaillée de l'Entreprise",
      descriptionPlaceholder: "Décrivez votre entreprise en détail (minimum 50 caractères)",
      descriptionHint: "Minimum 50 caractères requis",
      reasonForSale: "Raison de la Vente",
      reasonForSalePlaceholder: "Expliquez pourquoi vous vendez l'entreprise",
      industry: "Industrie / Secteur",
      industryPlaceholder: "Sélectionnez l'industrie...",
      naicsCode: "Code NAICS",
      naicsCodePlaceholder: "Code de classification industrielle",
      city: "Ville",
      cityPlaceholder: "Entrez la ville",
      state: "Province / État",
      statePlaceholder: "Entrez la province ou l'état",
      country: "Pays",
      countryPlaceholder: "Sélectionnez le pays...",
      yearEstablished: "Année de Création",
      yearEstablishedHint: "L'entreprise doit avoir au moins 3 ans pour être éligible.",
      previouslyListed: "Cette entreprise a-t-elle été mise sur le marché dans les 3 dernières années ?",
      previouslyListedYes: "Oui",
      previouslyListedNo: "Non",
      previouslyListedNote: "Cette information est utilisée exclusivement par AcquizGroup. Elle ne sera pas publiée publiquement, mais nous aide à développer des services mieux adaptés aux besoins des vendeurs.",
      representedSeller: "Vendeur Représenté",
      representedSellerHint: "Sélectionnez le vendeur pour lequel vous soumettez ce deal",
      representedSellerPlaceholder: "Sélectionnez un vendeur représenté...",
      noRepresentedSellers: "Vous n'avez encore aucun vendeur représenté. Ajoutez-en un depuis votre tableau de bord.",
    },

    // Step 2: Operations & Owner Dependency
    step2: {
      sectionTitle: "Opérations et Dépendance au Propriétaire",
      fullTimeEmployees: "Employés à Temps Plein",
      partTimeEmployees: "Employés à Temps Partiel",
      dailyOperationsDescription: "Description des Opérations Quotidiennes",
      dailyOperationsPlaceholder: "Décrivez comment l'entreprise fonctionne au quotidien",
      processDocumentationLevel: "Niveau de Documentation des Processus",
      documentationLevels: {
        1: "1 - Aucune documentation",
        2: "2 - Documentation minimale",
        3: "3 - Documentation partielle",
        4: "4 - Bien documenté",
        5: "5 - Entièrement documenté (SOPs complets)",
      },
      customerConcentration: "Concentration Client (%)",
      customerConcentrationHint: "Pourcentage du CA provenant du plus gros client",
      ownerInvolvement: "Implication du Propriétaire",
      ownerHoursPerWeek: "Heures du Propriétaire par Semaine",
      ownerType: "Type de Propriétaire",
      ownerTypes: {
        active: "Actif (présence quotidienne)",
        semiActive: "Semi-actif (présence partielle)",
        absentee: "Absent (délégation totale)",
      },
      ownerCriticalTasks: "Tâches Critiques du Propriétaire",
      ownerCriticalTasksPlaceholder: "Décrivez les tâches que seul le propriétaire peut effectuer",
      ownerAbsenceImpact: "Impact de l'Absence du Propriétaire",
      ownerAbsenceImpactPlaceholder: "Que se passe-t-il si le propriétaire est absent pendant une longue période?",
    },

    // Step 3: Licenses & Compliance
    step3: {
      sectionTitle: "Licences, Conformité et Technologie",
      specialLicensesRequired: "Des licences spéciales sont-elles requises pour exploiter cette entreprise?",
      yes: "Oui",
      no: "Non",
      licenseDetails: "Détails des Licences et Permis",
      licenseDetailsPlaceholder: "Décrivez les licences et permis requis",
      regulatoryRequirements: "Exigences Réglementaires",
      regulatoryRequirementsPlaceholder: "Décrivez les exigences réglementaires",
      legalDisputes: "Litiges Juridiques Passés ou en Cours",
      legalDisputesPlaceholder: "Décrivez tout litige juridique (passé ou en cours)",
      technologySectionTitle: "Technologie et Outils",
      hasProprietaryTech: "L'entreprise possède-t-elle une technologie propriétaire (plateforme, logiciel, application)?",
      proprietaryTechDescription: "Description de la Technologie Propriétaire",
      proprietaryTechDescriptionPlaceholder: "Décrivez la plateforme, le logiciel ou les outils que vous avez développés",
      techStackDescription: "Pile Technologique",
      techStackDescriptionPlaceholder: "ex. : langages, frameworks, fournisseur infonuagique, intégrations clés",
      thirdPartyTools: "Logiciels et Outils Tiers Utilisés",
      thirdPartyToolsPlaceholder: "Listez les logiciels/outils utilisés pour exploiter l'entreprise (CRM, comptabilité, paiements, etc.)",
      toolsTransferable: "Ces abonnements/licences sont-ils transférables à un nouveau propriétaire?",
      requiredBuyerTechSkills: "Compétences Techniques Requises de l'Acheteur",
      requiredBuyerTechSkillsPlaceholder: "ex. : « Aucun code requis » ou « Connaissance de base de X nécessaire »",
      hostingProvider: "Fournisseur d'Hébergement (si applicable)",
      hostingProviderPlaceholder: "ex. : AWS, Firebase, Shopify",
    },

    // Step 4: Assets & Balance Sheet
    step4: {
      sectionTitle: "Actifs et Bilan",
      inventoryValue: "Valeur de l'Inventaire ($)",
      ffeValue: "Valeur FF&E ($)",
      ffeValueHint: "Mobilier, Agencements et Équipements",
      accountsReceivable: "Comptes Clients ($)",
      otherCurrentAssets: "Autres Actifs Courants ($)",
      patentsOrTrademarks: "L'entreprise possède-t-elle des brevets ou marques déposées?",
      intellectualPropertyDetails: "Détails de la Propriété Intellectuelle",
      intellectualPropertyPlaceholder: "Décrivez les brevets, marques ou technologies propriétaires",
    },

    // Step 5: Real Estate
    step5: {
      sectionTitle: "Immobilier",
      isIncluded: "L'immobilier est-il inclus dans la transaction?",
      includedOptions: {
        yes: "Oui, l'immobilier est inclus",
        no: "Non, l'entreprise loue ses locaux",
      },
      realEstateAskPrice: "Prix Demandé pour l'Immobilier ($)",
      monthlyRent: "Loyer Mensuel ($)",
      leaseExpirationDate: "Date d'Expiration du Bail",
    },

    // Step 6: Financial Statements
    step6: {
      sectionTitle: "États Financiers",
      askingPrice: "Prix Demandé pour l'Entreprise ($)",
      askingPriceHint: "Prix demandé pour l'entreprise (hors immobilier si séparé)",
      revenueSection: "Chiffre d'Affaires (3 Dernières Années)",
      revenueYear1: "Année 1 (la plus récente)",
      revenueYear2: "Année 2",
      revenueYear3: "Année 3",
      ebitdaSection: "EBITDA (3 Dernières Années)",
      ebitdaYear1: "EBITDA Année 1",
      ebitdaYear2: "EBITDA Année 2",
      ebitdaYear3: "EBITDA Année 3",
      debtEquitySection: "Dettes et Capitaux Propres",
      cashFlowEquity: "Flux de Trésorerie / Capitaux Propres ($)",
      cashFlowEquityHint: "Ce montant DOIT être positif. Les entreprises avec un flux de trésorerie négatif seront AUTOMATIQUEMENT REJETÉES.",
      totalLiabilities: "Total des Passifs ($)",
      inheritedDebt: "Dette Assumée par l'Acheteur ($)",
      inheritedDebtHint: "Dette que le nouvel acheteur devra assumer",
    },

    // Step 7: Add-Backs
    step7: {
      sectionTitle: "Add-Backs (Ajustements EBITDA)",
      sectionHint: "Les add-backs représentent les dépenses discrétionnaires ou personnelles du propriétaire actuel que le nouveau propriétaire n'encourra probablement pas.",
      ownerSalary: "Salaire du Propriétaire ($)",
      autoExpense: "Frais de Véhicule Personnel ($)",
      travelEntertainment: "Voyages et Représentation ($)",
      nonRecurringLegal: "Frais Juridiques Non-Récurrents ($)",
      otherDiscretionary: "Autres Dépenses Discrétionnaires ($)",
      addbackRationale: "Justification Détaillée des Add-Backs",
      addbackRationalePlaceholder: "Fournissez une justification détaillée pour chaque add-back réclamé",
    },

    // Step 8: Projections & Growth
    step8: {
      sectionTitle: "Projections et Croissance",
      revenueGrowthForecast: "Prévision de Croissance du CA (%)",
      revenueGrowthHint: "Pourcentage de croissance annuelle attendu du chiffre d'affaires",
      ebitdaGrowthForecast: "Prévision de Croissance EBITDA (%)",
      ebitdaGrowthHint: "Pourcentage de croissance annuelle attendu de l'EBITDA",
      growthOpportunities: "Opportunités de Croissance Identifiées",
      growthOpportunitiesPlaceholder: "Décrivez les opportunités spécifiques de croissance",
      sellerFinancing: "Financement Vendeur",
      sellerFinancingOffered: "Êtes-vous ouvert au financement vendeur?",
      sellerFinancingDetails: "Détails du Financement Vendeur Proposé",
      sellerFinancingPlaceholder: "Décrivez les conditions envisagées (acompte %, taux d'intérêt, durée)",
      sfAmount: "Montant du financement vendeur ($)",
      sfAmountHint: "Montant total que vous êtes prêt à financer",
      sfPercentage: "Pourcentage du prix de vente (%)",
      sfPercentageHint: "Part du prix demandé couverte par le financement vendeur",
      sfInterestRate: "Taux d'intérêt (%)",
      sfInterestRateHint: "Taux d'intérêt annuel sur le billet vendeur",
      sfTermMonths: "Durée du prêt (mois)",
      sfTermHint: "Durée du billet de financement vendeur",
      sfDownPayment: "Acompte requis (%)",
      sfDownPaymentHint: "Acompte minimum exigé de l'acheteur",
      sfStandby: "Période de différé (mois)",
      sfStandbyHint: "Période sans paiements exigés (0 si aucune)",
      sfCollateral: "Garantie / Sûreté",
      sfCollateralHint: "Qu'est-ce qui garantit le billet vendeur?",
      sfCollateralOptions: {
        businessAssets: "Actifs de l'entreprise",
        personalGuarantee: "Garantie personnelle",
        realEstate: "Immobilier",
        stockPledge: "Nantissement d'actions / parts",
        none: "Aucune / Non garanti",
      },
      sfNotes: "Conditions et termes supplémentaires",
      sfNotesPlaceholder: "Conditions spéciales, clauses d'earn-out, déclencheurs de performance, etc.",
    },

    // Step 9: Culture & Transition
    step9: {
      sectionTitle: "Culture et Transition",
      coreValues: "Valeurs Fondamentales de l'Entreprise (jusqu'à 5)",
      coreValuesHint: "Les valeurs fondamentales sont les principes directeurs non négociables qui définissent l'identité, la culture et les processus décisionnels de votre organisation.",
      coreValueLabels: ["Intégrité", "Orientation Client", "Innovation", "Responsabilité", "Collaboration / Travail d'équipe"],
      coreValuePlaceholder: "Décrivez cette valeur...",
      coreValueDescriptionPlaceholder: "Décrivez comment cette valeur est appliquée dans votre entreprise...",
      managementTeam: "Équipe de direction",
      managementTeamExplanation: "Veuillez expliquer la structure de votre équipe de direction, les rôles clés et ses forces",
      idealSuccessor: "Description du Successeur Idéal",
      idealSuccessorPlaceholder: "Décrivez le profil d'acheteur idéal pour votre entreprise",
      biggestFear: "Votre Plus Grande Préoccupation Concernant la Vente",
      biggestFearPlaceholder: "Qu'est-ce qui vous préoccupe le plus dans la vente?",
      transitionPeriod: "Période de Transition",
      postSaleConsulting: "Disponibilité pour Consultation Post-Vente",
      consultingOptions: {
        yes: "Oui",
        no: "Non",
        negotiable: "Négociable",
      },
      transitionDuration: "Durée de Transition Préférée",
      transitionOptions: {
        30: "30 jours",
        60: "60 jours",
        90: "90 jours",
        more: "Plus de 90 jours",
      },
      nonCompetePeriod: "Période de Non-Concurrence",
      nonCompetePeriodOptions: {
        none: "Aucune",
        "1_year": "1 an",
        "2_years": "2 ans",
        "3_years": "3 ans",
        "5_years": "5 ans",
        negotiable: "Négociable",
      },
      dealBreakers: "Conditions Non-Négociables (Points de Rupture)",
      dealBreakersPlaceholder: "Listez les conditions qui sont non-négociables pour vous",
    },

    // Step 10: SWOT Analysis
    step10: {
      sectionTitle: "Analyse SWOT",
      sectionHint: "Veuillez fournir une analyse détaillée des forces, faiblesses, opportunités et menaces de l'entreprise. Minimum 10 caractères par section.",
      strengths: "Forces",
      strengthsPlaceholder: "Décrivez les principales forces de votre entreprise",
      weaknesses: "Faiblesses",
      weaknessesPlaceholder: "Décrivez les principales faiblesses ou défis",
      opportunities: "Opportunités",
      opportunitiesPlaceholder: "Décrivez les opportunités de croissance et le potentiel du marché",
      threats: "Menaces",
      threatsPlaceholder: "Décrivez les menaces externes et les défis concurrentiels",
    },

    // Step 11: Documents & Confidentiality
    step11: {
      sectionTitle: "Documents et Confidentialité",
      sectionHint: "Ces paramètres contrôlent la visibilité et l'accès aux informations de votre deal dans le Deal Monitor.",
      confidentialityLevel: "Niveau de Confidentialité",
      confidentialityOptions: {
        confidential: "Confidentiel - Accès sur demande uniquement",
        public: "Public - Visible par tous les acheteurs qualifiés",
      },
      confidentialityNote: "Tous les deals sont traités de manière confidentielle pour protéger vos informations sensibles.",
      lendersShareConsent: "Les informations de votre deal seront partagées avec les prêteurs qualifiés de notre réseau",
      lendersShareConsentHint: "Cela permet une mise en relation automatique avec les prêteurs appropriés pour votre deal — une étape standard de chaque annonce sur AcquizGroup™.",
      documentUpload: "Téléchargement de Documents",
      financialDocuments: "Documents Financiers et Juridiques",
      financialDocumentsHint: "Formats acceptés: PDF, Word, Excel",
      businessPhotos: "Photos de l'Entreprise",
      businessLogo: "Logo de l'Entreprise",
      businessLogoHint: "Format carré recommandé (300x300px minimum)",
      remove: "Supprimer",
      anonymization: "Anonymisation de l'Annonce Publique",
      hideLegalName: "Masquer la raison sociale",
      hideExactLocation: "Masquer l'emplacement exact",
      hideOwnerName: "Masquer le nom du propriétaire",
    },

    // Validation Messages
    validation: {
      required: "Ce champ est requis",
      legalNameRequired: "La raison sociale est requise",
      legalNameMinLength: "La raison sociale doit contenir au moins 2 caractères",
      entityTypeRequired: "Le type d'entité est requis",
      titleRequired: "Le titre du deal est requis",
      titleMinLength: "Le titre doit contenir au moins 3 caractères",
      descriptionRequired: "La description est requise",
      descriptionMinLength: "La description doit contenir au moins 50 caractères",
      industryRequired: "L'industrie est requise",
      cityRequired: "La ville est requise",
      countryRequired: "Le pays est requis",
      yearEstablishedRequired: "L'année de création est requise",
      businessTooYoung: "L'entreprise doit avoir au moins 3 ans. La soumission sera AUTOMATIQUEMENT REJETÉE.",
      ownerHoursInvalid: "Nombre d'heures invalide (0-168)",
      askingPriceMinimum: "Prix demandé minimum de 1 000$",
      cashFlowPositive: "Le flux de trésorerie / capitaux propres DOIT être positif. Valeur négative = REJET AUTOMATIQUE.",
      swotMinLength: "{field} requis (minimum 10 caractères)",
    },

    // Industries (matching seller registration)
    industries: [
      "Agriculture, Agro-alimentaire & Industrie alimentaire",
      "Automobile & Bateau / Marine",
      "Beauté & Soins personnels",
      "Commerce de détail (Général, Sport, Spécialisé)",
      "Communication, Médias & Relations publiques",
      "Conseil en génie & Technique (Civil, Électrique, Chimique, Aéro)",
      "Construction & Bâtiment (incl. Métiers & CVC)",
      "Divertissement, Loisirs & Sport",
      "Éducation & Services aux enfants",
      "En ligne & Technologie (SaaS, Dév. Web, Services TI)",
      "Entreprises de services (Général)",
      "Fabrication & Production industrielle",
      "Firme comptable & Fiscale (CPA, CA)",
      "Firme juridique (Corporatif, PI, Immobilier)",
      "Gestion des déchets, Traitement de l'eau & Environnement",
      "Grossistes & Distributeurs",
      "Intelligence d'affaires & Conseil en données",
      "Non classifiable / Autre",
      "Recrutement & Ressources humaines",
      "Restaurants & Services alimentaires",
      "Services à domicile (Contracteurs Résidentiels & Commerciaux)",
      "Services financiers & Assurance",
      "Services immobiliers & Gestion de propriétés",
      "Services pour animaux",
      "Soins de santé, Médical & Sciences de la vie",
      "Transport, Camionnage & Logistique",
      "Voyage, Tourisme & Hôtellerie",
    ],

    // Countries
    countries: [
      "Canada",
      "États-Unis",
      "France",
      "Royaume-Uni",
      "Allemagne",
      "Australie",
      "Autre",
    ],

    // Success/Error Messages
    messages: {
      draftSaved: "Brouillon sauvegardé avec succès!",
      draftSavedDescription: "Vous pouvez reprendre votre soumission plus tard.",
      submitSuccess: "Deal soumis avec succès!",
      submitSuccessDescription: "Votre deal a été soumis pour examen.",
      submitError: "Échec de la soumission. Veuillez réessayer.",
      authRequired: "Veuillez vous connecter pour soumettre un deal",
    },
  },
};

export type SubmitDealTranslations = typeof SUBMIT_DEAL_TRANSLATIONS.en;